import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Estimate, EstimateProjectCustomer, ItemizedEstimate, EstimateTotal } from './estimateType';
import EstimateApi from './estimateApi';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { formatCurrency } from '../lib/global_function';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select } from '../components/ui/select';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Save, Trash2, Plus, Package } from 'lucide-react';

const UpdateEstimatePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Estimate', path: '/main/project/estimate' },
    { label: 'Update', path: '/main/project/estimate/update' },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const estimate: EstimateProjectCustomer | undefined = location.state?.estimate;

  const [itemizedEstimates, setItemizedEstimates] = useState<ItemizedEstimate[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [totals, setTotals] = useState<EstimateTotal>({
    total_dollar: 0,
    total_hour: 0,
    non_optional_dollar: 0,
    non_optional_hour: 0,
    optional_dollar: 0,
    optional_hour: 0,
    tax_rate: 0,
    tax_amount: 0,
    discount_usd: 0,
    final_total: 0,
  });

  const [estimateEntered, setEstimateEntered] = useState({
    name: estimate?.name || '',
    description: estimate?.description || '',
    terms_and_conditions: estimate?.terms_and_conditions || '',
    time_to_complete_day: estimate?.time_to_complete_day || 0,
    time_to_complete_hr: estimate?.time_to_complete_hr || 0,
    tax_rate: estimate?.tax_rate || 0,
    discount_usd: estimate?.discount_usd || 0,
  });

  // Check if estimate is available
  useEffect(() => {
    if (!estimate) {
      setErrorMessage('Estimate not available, please click the Go To Estimate button to select an estimate.');
    }
  }, [estimate]);

  useEffect(() => {
    const fetchEstimateData = async () => {
      if (!estimate) return;

      try {
        // Use the new combined endpoint that returns estimate, line items, and totals
        const { estimate: estimateData, itemizedEstimates, totals: apiTotals } = 
          await EstimateApi.getEstimate(estimate.estimate_id);
        
        // Update estimate details
        setEstimateEntered({
          name: estimateData.name,
          description: estimateData.description,
          terms_and_conditions: estimateData.terms_and_conditions,
          time_to_complete_day: estimateData.time_to_complete_day,
          time_to_complete_hr: estimateData.time_to_complete_hr,
          tax_rate: estimateData.tax_rate,
          discount_usd: estimateData.discount_usd,
        });

        // Update line items
        setItemizedEstimates(itemizedEstimates);
        
        // Update totals
        setTotals(apiTotals);
      }
      catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      }
    };
    fetchEstimateData();
  }, [estimate]);

  // Dynamic calculation of totals whenever itemizedEstimates or estimateEntered changes
  useEffect(() => {
    const calculateTotals = () => {
      // Calculate subtotal from all itemized estimates
      const subtotal = itemizedEstimates.reduce((sum, item) => {
        return sum + (item.unit_price_dollar * item.quantity);
      }, 0);

      // Calculate optional and non-optional totals
      const optionalTotal = itemizedEstimates
        .filter(item => item.is_optional)
        .reduce((sum, item) => sum + (item.unit_price_dollar * item.quantity), 0);

      const nonOptionalTotal = itemizedEstimates
        .filter(item => !item.is_optional)
        .reduce((sum, item) => sum + (item.unit_price_dollar * item.quantity), 0);

      // Calculate tax amount
      const taxAmount = subtotal * (estimateEntered.tax_rate / 100);

      // Calculate final total
      const finalTotal = subtotal + taxAmount - estimateEntered.discount_usd;

      // Update totals state
      setTotals({
        total_dollar: subtotal,
        total_hour: 0,
        non_optional_dollar: nonOptionalTotal,
        non_optional_hour: 0,
        optional_dollar: optionalTotal,
        optional_hour: 0,
        tax_rate: estimateEntered.tax_rate,
        tax_amount: taxAmount,
        discount_usd: estimateEntered.discount_usd,
        final_total: finalTotal,
      });
    };

    calculateTotals();
  }, [itemizedEstimates, estimateEntered.tax_rate, estimateEntered.discount_usd]);

  const handleItemizedChange = (id: string, field: keyof ItemizedEstimate, value: any) => {
    setItemizedEstimates((prev) =>
      prev.map((item) =>
        (item.itemized_estimate_id === id || (item.itemized_estimate_id === '' && id === ''))
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const refreshItemizedEstimates = async () => {
    if (!estimate) return;

    try {
      // Use the new combined endpoint
      const { estimate: estimateData, itemizedEstimates, totals: apiTotals } = 
        await EstimateApi.getEstimate(estimate.estimate_id);
      
      // Update estimate details with latest from server
      setEstimateEntered({
        name: estimateData.name,
        description: estimateData.description,
        terms_and_conditions: estimateData.terms_and_conditions,
        time_to_complete_day: estimateData.time_to_complete_day,
        time_to_complete_hr: estimateData.time_to_complete_hr,
        tax_rate: estimateData.tax_rate,
        discount_usd: estimateData.discount_usd,
      });

      setItemizedEstimates(itemizedEstimates);
      setTotals(apiTotals);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const handleDeleteItemizedEstimate = async (id: string) => {
    const item = itemizedEstimates.find((item) => item.itemized_estimate_id === id);
    if (!item) return;

    setErrorMessage('');
    setMessage('');

    try {
      if (item.itemized_estimate_id === '') {
        setItemizedEstimates((prev) => prev.filter((i) => i !== item));
        setMessage('Unsaved itemized estimate removed.');
      } else {
        await EstimateApi.deleteItemizedEstimate(id);
        setItemizedEstimates((prev) => prev.filter((i) => i.itemized_estimate_id !== id));
        setMessage(`Itemized estimate deleted successfully.`);
        // Refresh the estimate summary after deletion
        await refreshItemizedEstimates();
      }
    }
    catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const addNewLine = () => {
    if (!estimate) return;

    const newItem: ItemizedEstimate = {
      itemized_estimate_id: '',
      estimate_id: estimate.estimate_id,
      code: '',
      description: '',
      unit_price_dollar: 0,
      quantity: 0,
      sub_total_dollar: 0,
      cost_type: 'all',
      duration_hour: 0,
      is_optional: false,
    };
    setItemizedEstimates((prev) => [...prev, newItem]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!estimate) return;

    setErrorMessage('');
    setMessage('');

    if (!estimateEntered.name.trim()) {
      setErrorMessage('Estimate name is required');
      return;
    }

    // Validate all line items before saving
    for (const item of itemizedEstimates) {
      if (item.description.trim().length === 0) {
        setErrorMessage('All line items must have a description.');
        return;
      }
      const subTotal = item.unit_price_dollar * item.quantity;
      if (subTotal === 0) {
        setErrorMessage('All line items must have unit price and quantity greater than zero.');
        return;
      }
    }

    try {
      const updatedEstimate: Partial<Estimate> = {
        name: estimateEntered.name,
        description: estimateEntered.description,
        time_to_complete_day: estimateEntered.time_to_complete_day,
        time_to_complete_hr: estimateEntered.time_to_complete_hr,
        terms_and_conditions: estimateEntered.terms_and_conditions,
        tax_rate: estimateEntered.tax_rate,
        discount_usd: estimateEntered.discount_usd,
      };

      // Prepare itemized estimates with calculated subtotals and rounded prices
      const itemizedData = itemizedEstimates.map(item => {
        // Round unit price to 2 decimal places
        const roundedPrice = Math.round(item.unit_price_dollar * 100) / 100;
        return {
          ...item,
          unit_price_dollar: roundedPrice,
          sub_total_dollar: roundedPrice * item.quantity,
        };
      });

      // Call the new combined endpoint
      await EstimateApi.updateEstimateWithLineItems(
        estimate.estimate_id,
        updatedEstimate,
        itemizedData
      );

      setMessage('Estimate and all line items saved successfully!');
      
      // Refresh to get updated data from server
      // This now also updates the estimate form fields
      await refreshItemizedEstimates();
    }
    catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const handleNavigateToEstimateList = () => {
    navigate('/main/project/estimate');
  };

  return (
    <>
      <HorizontalMenu />
      <div className="px-4 py-6 max-w-7xl mx-auto">
        <Breadcrumbs items={breadcrumbs} />

        <h1 className="text-3xl font-bold text-foreground mt-6 mb-6">Update Estimate</h1>

        {errorMessage && (
          <Alert variant="error" className="mb-6">
            <p>{errorMessage}</p>
            {!estimate && (
              <Button onClick={handleNavigateToEstimateList} className="mt-3">
                Go to Estimate
              </Button>
            )}
          </Alert>
        )}

        {message && (
          <Alert variant="success" className="mb-6">
            {message}
          </Alert>
        )}

        {estimate ? (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Form Section - 2 columns */}
              <div className="lg:col-span-2 space-y-6">
                {/* Estimate Details Section */}
                <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <h2 className="text-xl font-semibold text-foreground mb-4">Estimate Details</h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Estimate Name <span className="text-destructive">*</span>
                      </label>
                      <Input
                        type="text"
                        value={estimateEntered.name}
                        onChange={(e) => setEstimateEntered({ ...estimateEntered, name: e.target.value })}
                        required
                        placeholder="Enter estimate name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Description
                      </label>
                      <Input
                        type="text"
                        value={estimateEntered.description}
                        onChange={(e) => setEstimateEntered({ ...estimateEntered, description: e.target.value })}
                        placeholder="Enter description"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Time to Complete
                        </label>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="number"
                            value={estimateEntered.time_to_complete_day === 0 ? '' : estimateEntered.time_to_complete_day}
                            onChange={(e) => {
                              const inputValue = parseFloat(e.target.value) || 0;
                              const cappedValue = Math.min(inputValue, 365);
                              setEstimateEntered({
                                ...estimateEntered,
                                time_to_complete_day: cappedValue
                              });
                            }}
                            min="0"
                            max="365"
                            step="1"
                            placeholder="0"
                            className="w-24"
                          />
                          <span className="text-sm text-paragraph">Days</span>
                          <Input
                            type="number"
                            value={estimateEntered.time_to_complete_hr === 0 ? '' : estimateEntered.time_to_complete_hr}
                            onChange={(e) => {
                              const inputValue = parseFloat(e.target.value) || 0;
                              const cappedValue = Math.min(inputValue, 23);
                              setEstimateEntered({
                                ...estimateEntered,
                                time_to_complete_hr: cappedValue
                              });
                            }}
                            min={0}
                            max={23}
                            step="1"
                            placeholder="0"
                            className="w-24"
                          />
                          <span className="text-sm text-paragraph">Hours</span>
                        </div>
                      </div>

                      <div className="col-span-3">
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Tax & Discount
                        </label>
                        <div className="flex gap-2 items-center">
                          <span className="text-sm text-paragraph">Tax Rate (%): </span>
                          <Input
                            type="number"
                            value={estimateEntered.tax_rate === 0 ? '' : estimateEntered.tax_rate}
                            onChange={(e) => {
                              const inputValue = parseFloat(e.target.value) || 0;
                              const cappedValue = Math.min(inputValue, 50);
                              setEstimateEntered({
                                ...estimateEntered,
                                tax_rate: cappedValue
                              });
                            }}
                            min="0"
                            max="50"
                            step="0.1"
                            placeholder="0"
                            className="w-24"
                          />

                          <span className="text-sm text-paragraph">Discount ($): </span>
                          <Input
                            type="number"
                            value={estimateEntered.discount_usd === 0 ? '' : estimateEntered.discount_usd}
                            onChange={(e) => {
                              const inputValue = parseFloat(e.target.value) || 0;
                              const cappedValue = Math.min(inputValue, 10000);
                              setEstimateEntered({
                                ...estimateEntered,
                                discount_usd: cappedValue
                              });
                            }}
                            min="0"
                            max="10000"
                            step="10"
                            placeholder="0"
                            className="w-32"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Terms and Conditions
                      </label>
                      <Textarea
                        value={estimateEntered.terms_and_conditions}
                        onChange={(e) => setEstimateEntered({ ...estimateEntered, terms_and_conditions: e.target.value })}
                        placeholder="Enter terms and conditions"
                        className="min-h-24"
                      />
                    </div>
                  </div>
                </div>

                {/* Line Items Section */}
                <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-foreground">Line Items</h2>
                    <Button type="button" onClick={addNewLine} size="medium" variant="inverted">
                      <Plus className="size-5" />
                      Add Line Item
                    </Button>
                  </div>

                  {itemizedEstimates.length === 0 ? (
                    <div className="text-center py-12 text-paragraph">
                      <Package className="size-12 mx-auto mb-3 text-muted" />
                      <p>No line items yet. Click "Add Line Item" to get started.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {itemizedEstimates.map((item, index) => (
                        <div
                          key={item.itemized_estimate_id || `new-${index}`}
                          className="border-2 border-muted rounded-lg p-4 hover:border-primary/20 transition-colors"
                        >
                          {/* Row 1: Code, Description, Cost Type, Optional, Delete */}
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Code</label>
                              <Input
                                placeholder="Code"
                                value={item.code}
                                onChange={(e) =>
                                  handleItemizedChange(item.itemized_estimate_id || '', 'code', e.target.value)
                                }
                                className="text-sm"
                              />
                            </div>

                            <div className="md:col-span-4">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Description <span className="text-destructive">*</span>
                              </label>
                              <Input
                                placeholder="Enter description"
                                value={item.description}
                                onChange={(e) =>
                                  handleItemizedChange(item.itemized_estimate_id || '', 'description', e.target.value)
                                }
                                className="text-sm"
                              />
                            </div>

                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Cost Type</label>
                              <Select
                                value={item.cost_type}
                                onChange={(e) =>
                                  handleItemizedChange(item.itemized_estimate_id || '', 'cost_type', e.target.value)
                                }
                                className="text-sm h-9"
                              >
                                <option value="all">All</option>
                                <option value="labor">Labor</option>
                                <option value="material">Material</option>
                              </Select>
                            </div>

                            <div className="md:col-span-2">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Optional</label>
                              <Select
                                value={item.is_optional ? 'true' : 'false'}
                                onChange={(e) =>
                                  handleItemizedChange(
                                    item.itemized_estimate_id || '',
                                    'is_optional',
                                    e.target.value === 'true'
                                  )
                                }
                                className="text-sm h-9"
                              >
                                <option value="false">No</option>
                                <option value="true">Yes</option>
                              </Select>
                            </div>

                            <div className="md:col-span-2 flex items-end">
                              <Button
                                type="button"
                                size="medium"
                                variant="inverted"
                                onClick={() => handleDeleteItemizedEstimate(item.itemized_estimate_id || '')}
                                className="w-full"
                              >
                                <Trash2 className="size-4" />
                                Delete
                              </Button>
                            </div>
                          </div>

                          {/* Row 2: Unit Price, Quantity, Subtotal, Badge */}
                          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                            <div className="md:col-span-3">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Unit Price <span className="text-destructive">*</span>
                              </label>
                              <Input
                                type="number"
                                placeholder="0.00"
                                value={item.unit_price_dollar === 0 ? '' : item.unit_price_dollar}
                                onChange={(e) => {
                                  const inputValue = parseFloat(e.target.value) || 0;
                                  handleItemizedChange(item.itemized_estimate_id, 'unit_price_dollar', inputValue);
                                }}
                                min="0"
                                step="0.01"
                                className="text-sm"
                              />
                            </div>

                            <div className="md:col-span-3">
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Quantity <span className="text-destructive">*</span>
                              </label>
                              <Input
                                type="number"
                                placeholder="0"
                                value={item.quantity === 0 ? '' : item.quantity}
                                onChange={(e) => {
                                  const inputValue = parseFloat(e.target.value) || 0;
                                  const cappedValue = Math.max(0, Math.min(inputValue, 10000));
                                  handleItemizedChange(item.itemized_estimate_id, 'quantity', cappedValue);
                                }}
                                min="0"
                                className="text-sm"
                              />
                            </div>

                            <div className="md:col-span-3">
                              <label className="block text-xs font-medium text-gray-600 mb-1">Subtotal</label>
                              <div className="h-9 flex items-center px-3 bg-gray-50 rounded-2xl border-2 border-muted">
                                <span className="text-sm font-semibold text-foreground">
                                  ${formatCurrency((item.unit_price_dollar * item.quantity).toFixed(2))}
                                </span>
                              </div>
                            </div>

                            <div className="md:col-span-3">
                              {item.is_optional && (
                                <Badge variant="warning">Optional</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Save Button */}
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    size="medium"
                    variant="inverted"
                    onClick={handleNavigateToEstimateList}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" size="medium">
                    <Save className="size-5" />
                    Save All Changes
                  </Button>
                </div>
              </div>

              {/* Sticky Estimate Summary - 1 column */}
              <div className="lg:col-span-1">
                <div className="sticky top-6 p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/30 rounded-lg shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Estimate Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Subtotal:</span>
                      <span className="font-semibold">${formatCurrency(Number(totals.total_dollar || 0).toFixed(2))}</span>
                    </div>
                    {Number(totals.tax_rate ?? 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Tax ({totals.tax_rate}%):</span>
                        <span className="font-semibold">${formatCurrency(Number(totals.tax_amount || 0).toFixed(2))}</span>
                      </div>
                    )}
                    {Number(totals.discount_usd ?? 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-700">Discount:</span>
                        <span className="font-semibold text-red-600">-${formatCurrency(Number(totals.discount_usd || 0).toFixed(2))}</span>
                      </div>
                    )}
                    <div className="pt-3 border-t-2 border-primary/30">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-foreground">Total:</span>
                        <span className="text-2xl font-bold text-primary">
                          ${formatCurrency(Number(totals.final_total || 0).toFixed(2))}
                        </span>
                      </div>
                    </div>
                    {Number(totals.optional_dollar ?? 0) > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-300 text-xs text-gray-600">
                        <div className="flex justify-between">
                          <span>Optional items:</span>
                          <span>${formatCurrency(Number(totals.optional_dollar || 0).toFixed(2))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Required items:</span>
                          <span>${formatCurrency(Number(totals.non_optional_dollar || 0).toFixed(2))}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div className="bg-white rounded-2xl border-2 border-muted p-12 text-center">
            <p className="text-paragraph">No estimate data available to display.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default UpdateEstimatePage;